/**
 * StatusFlow Component
 *
 * Pipeline-style navigation: bordered tabs joined by line arrows, one step after another.
 * Used to display and navigate document/invoice approval states.
 *
 * Visual: [Item 4] → [Item 7] → ┆[Parallel 3]┆ → [End 58]   [Detached 2]
 *                               ┆[Parallel 9]┆   [Next 140]   [Alternative 5]
 *
 * Accessibility: role="tablist" on container, role="tab" + aria-selected on each item.
 */
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '../utils';

export interface StatusFlowItem<T extends string = string> {
  /** Unique identifier — also used as the filter value */
  id: T;
  /** Human-readable label */
  label: string;
  /**
   * Count badge value.
   * Pass `undefined` to hide the badge (e.g. while loading).
   */
  count?: number;
  /**
   * Joins the previous item's step, stacked under it; a detached one under the previous detached.
   */
  alternative?: boolean;
  /** Joins the previous item's step as a state reached in parallel: the step gets a dashed frame. */
  parallel?: boolean;
  /** Shown outside the flow, after a gap, error-coloured. */
  detached?: boolean;
  /** Warning-coloured inside the flow, so a refused state stands out from the others. */
  warning?: boolean;
}

export interface StatusFlowProps<T extends string = string> {
  /** Ordered list of status items */
  items: StatusFlowItem<T>[];
  /** Currently selected item id; one no item carries selects none. */
  activeId: T;
  /** Called when user clicks an item */
  onSelect: (id: T) => void;
  /** Given, a click on the selected item calls it instead of `onSelect`, to select none. */
  onDeselect?: () => void;
  className?: string;
  testId?: string;
}

/**
 * Format count for display — collapses values ≥ 1000 to "999+"
 */
function formatCount(count: number): string {
  return count > 999 ? '999+' : String(count);
}

/** A short line with an arrowhead between two steps. */
function Arrow() {
  return (
    <span
      aria-hidden='true'
      data-status-flow-arrow=''
      className='relative mx-xs h-px w-4 shrink-0 bg-foreground-disabled'
    >
      <span className='absolute -top-0.5 right-0 size-1.5 rotate-45 border-t border-r border-foreground-disabled' />
    </span>
  );
}

/** Flow items grouped by step: an item opens a step, its alternatives and parallels join it. */
function groupSteps<T extends string>(items: StatusFlowItem<T>[]): StatusFlowItem<T>[][] {
  const steps: StatusFlowItem<T>[][] = [];
  for (const item of items) {
    const current = steps.at(-1);
    if ((item.alternative || item.parallel) && current) current.push(item);
    else steps.push([item]);
  }
  return steps;
}

export function StatusFlow<T extends string = string>({
  items,
  activeId,
  onSelect,
  onDeselect,
  className,
  testId
}: StatusFlowProps<T>) {
  const instanceId = React.useId();
  const layoutId = `status-flow-active-${instanceId}`;
  const flowItems = items.filter(item => !item.detached);
  const detachedItems = items.filter(item => item.detached);

  const renderTab = (item: StatusFlowItem<T>) => {
    const isActive = item.id === activeId;
    const isError = item.detached === true;
    const isWarning = !isError && item.warning === true;
    return (
      <button
        key={item.id}
        type='button'
        role='tab'
        aria-selected={isActive}
        onClick={() => (isActive && onDeselect ? onDeselect() : onSelect(item.id))}
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center gap-sm rounded-lg border',
          'px-sm py-xs md:px-md',
          'whitespace-nowrap transition-colors duration-150 cursor-pointer',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-base focus-visible:ring-offset-1',
          isActive
            ? cn(
                'font-semibold text-foreground-white',
                isError
                  ? 'border-error-border'
                  : isWarning
                    ? 'border-warn-text'
                    : 'border-primary-base'
              )
            : cn(
                'bg-background-input font-medium',
                isError
                  ? 'border-error-border text-error-text hover:bg-error-bg'
                  : isWarning
                    ? 'border-warn-border text-warn-text hover:bg-warn-bg'
                    : 'border-border-base text-foreground-muted hover:border-border-input hover:text-foreground-base'
              )
        )}
      >
        {/* Animated active background */}
        <AnimatePresence>
          {isActive && (
            <motion.span
              layoutId={layoutId}
              className={cn(
                'absolute -inset-px rounded-lg',
                isError ? 'bg-error-border' : isWarning ? 'bg-warn-text' : 'bg-primary-base'
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            />
          )}
        </AnimatePresence>

        {/* Label */}
        <span className='relative z-10 leading-5'>{item.label}</span>

        {/* Count badge */}
        {item.count !== undefined && (
          <span
            className={cn(
              'relative z-10 inline-flex h-5 min-w-5 items-center justify-center rounded-full',
              'px-sm text-xs font-semibold leading-none',
              isActive && 'bg-white',
              isActive &&
                (isError
                  ? 'text-error-text'
                  : isWarning
                    ? 'text-warn-text'
                    : 'text-foreground-base'),
              !isActive &&
                (isError
                  ? 'bg-error-bg text-error-text'
                  : isWarning
                    ? 'bg-warn-bg text-warn-text'
                    : 'bg-background-muted text-foreground-muted')
            )}
          >
            {formatCount(item.count)}
          </span>
        )}
      </button>
    );
  };

  const renderStep = (step: StatusFlowItem<T>[]) => {
    if (step.length === 1) return renderTab(step[0]);
    const parallel = step.some(item => item.parallel);
    return (
      <div
        data-status-flow-step={parallel ? 'parallel' : 'stack'}
        className={cn(
          'flex shrink-0 flex-col items-stretch gap-xs',
          parallel && 'rounded-xl border border-dashed border-foreground-disabled p-xs'
        )}
      >
        {step.map(item => renderTab(item))}
      </div>
    );
  };

  return (
    <nav
      role='tablist'
      aria-label='Status filter'
      data-testid={testId}
      className={cn('flex items-center flex-wrap gap-y-xs text-sm', className)}
    >
      {groupSteps(flowItems).map((step, index) => (
        <React.Fragment key={step[0].id}>
          {index > 0 && <Arrow />}
          {renderStep(step)}
        </React.Fragment>
      ))}

      {detachedItems.length > 0 && (
        <div data-status-flow-detached='' className='ml-xl flex shrink-0 items-center gap-xs'>
          {groupSteps(detachedItems).map(step => (
            <React.Fragment key={step[0].id}>{renderStep(step)}</React.Fragment>
          ))}
        </div>
      )}
    </nav>
  );
}

StatusFlow.displayName = 'StatusFlow';
